import { Component, EventEmitter, Output, Input, OnDestroy, OnChanges, ViewChild, OnInit, inject, DestroyRef } from '@angular/core';
import { Observable, Subscription } from 'rxjs';
import { take, tap } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { saveAs as importedSaveAs } from 'file-saver';
import { ToastrService } from 'ngx-toastr';
import * as _ from 'lodash';
import { NgbPopover } from '@ng-bootstrap/ng-bootstrap';

import { AppConstants } from '../../../app.constants';
import { WINDOW } from '../../../app.settings';
import { DeleteDialogService } from '../../../core/services/dialog-services/delete-dialog.service';
import { PlaylistItemsService } from '../../../core/services/http-services/playlistItems.service';
import { FilesService } from '../../../core/services/http-services/files.service';
import { PlaylistItemType } from '../../../core/models/enums/playlist-item-type.enum';
import { PlaylistDTO } from '../../../core/models/view-models/playlist-models/playlist-dto.model';
import { PlaylistItemDTO } from '../../../core/models/view-models/playlist-item-models/playlist-item-dto.model';
import { LaunchersService } from '../../../core/services/http-services/launchers.service';
import { ToastrMessageEnum } from '../../../core/enums/toastr-messages/toastr-message.enum';
import { PlaylistDialogMessagesEnum } from '../../../core/enums/toastr-messages/playlist-dialog-messages.enum';
import { MawChromeLauncherViewMode } from '../../../core/enums/mcl-view-mode.enum';
import { UIService } from '../../../core/services/utils/ui.service';

@Component({
  selector: 'playlist-item',
  templateUrl: './playlist-item.component.html',
  styleUrls: ['./playlist-item.component.scss']
})
export class PlaylistItemComponent implements OnDestroy, OnChanges, OnInit {
  @Output() itemDeleted: EventEmitter<number> = new EventEmitter();
  @Output() editItem: EventEmitter<void> = new EventEmitter();

  @Input() item: PlaylistItemDTO;
  @Input() playlist: PlaylistDTO;

  @ViewChild('popover') popover: NgbPopover;

  playlistItemsTypes = PlaylistItemType;
  isMawChromeLauncher: boolean;
  selectedMode: string;
  mawChromeParts: number;
  isPortraitView: boolean;
  displayPopoverTimer: number;
  delayPopoverTimer: number;
  appConst = AppConstants;

  showHoverElements = false;
  subscriptions: Subscription[] = [];

  itemsSelection$: Observable<boolean>;
  #window = inject(WINDOW);

  constructor(
    public playlistItemsService: PlaylistItemsService,
    public filesService: FilesService,
    public toastr: ToastrService,
    public deleteDialogService: DeleteDialogService,
    public launcherService: LaunchersService,
    private uiService: UIService,
    private destroyRef: DestroyRef
  ) { }

  ngOnInit(): void {
    this.filesService.setIcon(this.item, this.playlist.IsAndroid);
    this.itemsSelection$ = this.uiService.selectionInProgress.pipe(
      tap(() => clearTimeout(this.delayPopoverTimer)),
      takeUntilDestroyed(this.destroyRef)
    );
  }

  ngOnChanges(): void {
    if (this.playlist.IsAndroid) {
      this.isMawChromeLauncher = false;
    }

    if (this.item.Launcher?.ExecutableName?.toLowerCase().includes(AppConstants.CHROME_LAUNCHER_TITLE_PREFIX)) {
      const viewModesKeys = Object.keys(MawChromeLauncherViewMode);
      this.isMawChromeLauncher = true;
      this.mawChromeParts = this.item.PlaylistItemLinks.length;

      if (this.item.LayoutSplitViewType) {
        this.selectedMode = viewModesKeys.find(mawChromeMode =>
          +mawChromeMode.split(AppConstants.MCL_MODE_ID_DELIMITER)[1] === this.item.LayoutSplitViewType);
      }
      else {
        this.selectedMode = viewModesKeys[0];
      }
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(x => x.unsubscribe());
  }

  delete() {
    this.subscriptions.push(
      this.deleteDialogService.show('Deleting playlist item')
        .subscribe(result => {
          if (result) {
            _.remove(this.playlist.PlaylistItems, x => x == this.item);
            const isLocalItemExist: boolean = _.some(this.playlist.PlaylistItems, (playlistItem: PlaylistItemDTO) => playlistItem.IsLocal);
            if (!isLocalItemExist) {
              this.playlist.MachinePhysicalAddress = null;
            }
            this.itemDeleted.emit();
          }
        }));
  }

  download() {
    this.toastr.info(
      'Your file will be downloaded in a few seconds',
      'File Downloading'
    );

    this.filesService.downloadFile(this.item.PlaylistItemLinks[0].FileId)
      .pipe(take(1))
      .subscribe(blob => {
        importedSaveAs(blob, this.item.PlaylistFileName);
      });
  }

  duplicate() {
    this.toastr.success(
      `\'${this.item.PlaylistItemTitle}\' ${ToastrMessageEnum.SuccessfullyDuplicatedTitle}`,
      PlaylistDialogMessagesEnum.PlaylistItemDuplicationTitle
    );

    const launcher = this.item.Launcher;
    const launcherFeatures = this.item.launcherFeatures?.length ?
      this.item.launcherFeatures :
      launcher?.LauncherFeatures?.length ?
        launcher?.LauncherFeatures :
        [];

    if (AppConstants.FIT_SCREEN_ENABLED_LAUNCHERS.includes(launcher?.LauncherTitle) && !launcherFeatures.length) {
      this.launcherService.getLauncherFeatures(this.item.LauncherId, this.item.Id).pipe(take(1)).subscribe(
        features => {
          const duplicatedItem: PlaylistItemDTO = this.playlistItemsService.duplicateItem(this.item, features);
          this.playlist.PlaylistItems.push(duplicatedItem);
        });
    }
    else {
      const duplicatedItem: PlaylistItemDTO = this.playlistItemsService.duplicateItem(this.item, launcherFeatures);
      this.playlist.PlaylistItems.push(duplicatedItem);
    }
  }

  showContentPreview() {
    clearTimeout(this.displayPopoverTimer);
    this.delayPopoverTimer = this.#window.setTimeout(() => {
      this.displayPopoverTimer = this.filesService.previewContent(this.item, this.popover);
    }, AppConstants.CONTENT_PREVIEW_DELAY_TIMEOUT);
  }

  hideContentPreview() {
    this.popover.close();
    clearTimeout(this.displayPopoverTimer);
    clearTimeout(this.delayPopoverTimer);
  }
}
